// @ts-strict-ignore
import React, {useMemo} from 'react';
import produce from 'immer';
import clsx from 'clsx';

import {propertySelectionToSegmentation} from 'common/query-builder/query-block/segment-menu/util';
import {Behavior, Segmentation} from 'common/types/reports/query';
import {withStyles} from 'common/util/react';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {PropertyValuesScreenReact} from 'common/widgets/property-filter-menu/property-values-screen';
import {ButtonReact} from 'common/components/button';
import {Placement} from 'common/components/tooltip/types';
import {useTranslation} from 'common/i18n/hooks';

import css from './index.module.styl?inline';
import styles from './index.module.styl';

type Props = {
  selected: Segmentation;
  behavior: Behavior;
  operatorOpen: boolean;
  valueOpen: boolean;
  projectId: number;
  workspaceId: number;
  onOperatorOpen: () => void;
  onValueOpen: () => void;
  onChange: (selected: Segmentation) => void;
};

export const MultiAttributionBehaviorFilterSubItem = withStyles((props: Props) => {
  const {t} = useTranslation();
  const selectedFilter = useMemo(() => {
    if (props.behavior.filters?.[0]) {
      return new PropertyFilter(props.behavior.filters[0]);
    }

    // Show default property filter if in the process of adding initial touchpoint filter
    return new PropertyFilter({
      ...propertySelectionToSegmentation(props.behavior.property),
      filterOperator: StringOperator.DoesNotEqual,
      filterValue: [],
    });
  }, [props.behavior]);

  return (
    <div className={clsx(styles.container)}>
      <SelectReact
        className={clsx(styles.select, styles.filterOperator, `filter-operator`)}
        elref='filterOperator'
        open={props.operatorOpen}
        theme={SelectTheme.QbSubitem}
        size={ButtonSize.Small}
        sections={[
          {
            items: [
              {value: StringOperator.DoesNotEqual, label: t(`query-builder.attribution.touchpoints-exclude`)},
              {value: StringOperator.Equals, label: t(`query-builder.attribution.touchpoints-include`)},
            ].map((item) => {
              return {
                ...item,
                isSelected: item.value === selectedFilter.filterOperator,
              };
            }),
          },
        ]}
        menuWidth={-1}
        onMenuChange={(ev) => ev.detail.isOpen && props.onOperatorOpen()}
        onChange={(ev) =>
          props.onChange(
            produce(props.selected, (draft) => {
              draft.behavior.filters[0] = {
                ...selectedFilter,
                filterOperator: ev.detail.item.value,
              };
            }),
          )
        }
      />
      <SelectReact
        className={clsx(styles.select, `filter-value-select`)}
        elref='filterValueSelect'
        label={selectedFilter.isValid() ? selectedFilter.getValuesLabel() : t(`query-builder.value-placeholder`)}
        open={props.valueOpen}
        theme={SelectTheme.QbSubitem}
        size={ButtonSize.Small}
        onMenuChange={(ev) => ev.detail.isOpen && props.onValueOpen()}
      >
        {props.valueOpen && (
          <PropertyValuesScreenReact
            elref='filterValue'
            slot='body'
            propertyFilter={selectedFilter.getAttributes()}
            displayValueOnly
            hideUndefinedValue
            projectId={props.projectId}
            workspaceId={props.workspaceId}
            onCommitPropertyFilter={(ev) =>
              props.onChange(
                produce(props.selected, (draft) => {
                  draft.behavior.filters[0] = ev.detail.propertyFilter.getAttributes();
                }),
              )
            }
          />
        )}
      </SelectReact>
      <ButtonReact
        className={styles.deleteButton}
        theme={ButtonTheme.Tertiary}
        size={ButtonSize.Inline}
        icon='x'
        role='button'
        type={ButtonType.Destructive}
        tooltip={{
          text: t(`query-builder.attribution.remove-touchpoint-filter`),
          placement: Placement.Top,
        }}
        onClick={() =>
          props.onChange(
            produce(props.selected, (draft) => {
              draft.behavior.filters = [];
            }),
          )
        }
      />
    </div>
  );
}, css);
